import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { adminUsersService, AdminClientDailyNormsCalculation, DailyNormsStepBasis, DailyNormsStepValue } from '../../services/admin-users.service';
import { toastService } from '../../services/toast.service';
import { getAdminClientExpertNorms } from '../../services/settings.service';
import { ExpertNormsTable } from '../clients/daily-norms/ExpertNormsTable';

const MACRO_SHORT: Record<string, string> = { protein: 'Б', fat: 'Ж', carbs: 'В' };
const formatValue = (value: DailyNormsStepValue) =>
  typeof value === 'object' && value !== null
    ? Object.entries(value).map(([k, v]) => `${MACRO_SHORT[k] ?? k} ${v}`).join(' · ')
    : String(value);

/** Основа кроку - "дуже важливо бачити, де наукові цифри": наука зеленим, правило Everyday - помаранчевим. */
const BASIS: Record<DailyNormsStepBasis['type'], { label: string; color: string }> = {
  science: { label: 'Наука', color: 'success.main' },
  everyday: { label: 'Правило Everyday', color: 'warning.dark' },
  arithmetic: { label: 'Арифметика', color: 'text.secondary' },
};

const BasisLabel: React.FC<{ basis: DailyNormsStepBasis | null; withSource?: boolean }> = ({ basis, withSource }) => {
  if (!basis) return <>—</>;
  return (
    <Box component="span">
      <Typography component="span" variant="body2" fontWeight={700} color={BASIS[basis.type].color}>{BASIS[basis.type].label}</Typography>
      {withSource && basis.source && (
        <Typography variant="caption" color="text.disabled" display="block">{basis.source}</Typography>
      )}
    </Box>
  );
};

/** Числа ("реальні значення") - жирним. */
const withBoldNumbers = (line: string) =>
  line.split(/(\d+(?:[.,]\d+)?)/).filter(Boolean).map((part, i) => (/^\d/.test(part) ? <b key={i}>{part}</b> : part));

/** Рядок формули / обчислення: є число і знак рівності або множення (так само, як у PDF). */
const isFormula = (line: string) => /\d/.test(line) && /[=×*]/.test(line);

/** Пояснення калькулятора як є: формули - на сірому тлі, решта (коментарі, довідка) - бліда й дрібніша. */
const Explanation: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    {text.split('\n').map((line, i) => {
      if (!line.trim()) return <Box key={i} sx={{ height: 4 }} />;
      return isFormula(line)
        ? <Typography key={i} variant="body2" sx={{ bgcolor: 'action.hover', px: 1, py: 0.25, my: 0.25 }}>{withBoldNumbers(line)}</Typography>
        : <Typography key={i} variant="caption" color="text.disabled">{withBoldNumbers(line)}</Typography>;
    })}
  </Box>
);

/**
 * Вкладка "Розрахунок денних норм" (2026-09-30): щоб фахівцю було зручно перевірити денні норми клієнта. Факт - збережені
 * норми, поруч -
 * відтворення тим самим розрахунком за поточними даними з поясненнями калькуляторів; розбіжності підсвічено. Рядки,
 * підписи й розбіжності - з бекенду (ті самі, що в PDF). Лише перегляд.
 */
export const AdminClientDailyNormsTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [data, setData] = useState<AdminClientDailyNormsCalculation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    adminUsersService.getClientDailyNormsCalculation(clientId)
      .then((result) => { if (!cancelled) setData(result); })
      .catch(() => { if (!cancelled) setError('Не вдалося завантажити розрахунок денних норм'); });
    return () => { cancelled = true; };
  }, [clientId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await adminUsersService.downloadClientDailyNormsCalculationPdf(clientId);
    } catch {
      toastService.error('Не вдалося сформувати PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>;

  const mismatches = data.rows.filter((row) => row.mismatch).length;
  const errors = data.sections.filter((section) => section.error);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" startIcon={downloading ? <CircularProgress size={16} /> : <PictureAsPdf />} onClick={downloadPdf} disabled={downloading}>
          PDF
        </Button>
      </Box>
      {!data.hasFact && <Alert severity="info">Збережених норм нема - клієнт бачить прочерки.</Alert>}
      {data.hasFact && mismatches > 0 && (
        <Alert severity="warning">
          Розбіжностей: {mismatches}. Клієнт міг змінити норми вручну, або дані змінились після останнього перерахунку.
        </Alert>
      )}
      {errors.length > 0 && (
        <Alert severity="error">
          Розрахунок не вдався ({errors.map(({ title }) => title).join(', ')}) - застосовано запасні значення. Бракує вхідних даних.
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Ціль</TableCell>
            <TableCell align="right">Факт (у клієнта)</TableCell>
            <TableCell align="right">Розрахунок</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={row.label} sx={row.mismatch ? { bgcolor: 'warning.light' } : undefined}>
              <TableCell>{row.label}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{row.fact ?? '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{row.calculated ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.factUpdatedAt && (
        <Typography variant="caption" color="text.secondary">
          Норми оновлено: {new Date(data.factUpdatedAt).toLocaleString('uk-UA')}
        </Typography>
      )}

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Вхідні дані (профіль і останні вимірювання)</Typography>
        <Grid container spacing={1}>
          {data.inputs.map((input) => (
            <Grid item xs={6} sm={3} key={input.key}>
              <Typography variant="caption" color="text.secondary">{input.label}</Typography>
              <Typography variant="body2" fontWeight={700}>{input.value ?? '—'}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Ланцюжок розрахунку</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Крок</TableCell>
              <TableCell align="right">Значення</TableCell>
              <TableCell>Основа</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.summary.map((row, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Typography component="span" variant="body2" color="text.secondary">{row.section} · </Typography>
                  {row.name}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatValue(row.value)}</TableCell>
                <TableCell><BasisLabel basis={row.basis} withSource /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {data.sections.map((section) => (
        <Box key={section.key}>
          <Typography variant="subtitle2" gutterBottom>{section.title}</Typography>
          {section.error && <Alert severity="error" sx={{ mb: 1 }}>{section.error}</Alert>}
          {section.steps.map((step) => (
            <Box key={step.step} sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {step.step}. {step.name}: <b>{formatValue(step.value)}</b>{'  '}
                <BasisLabel basis={step.basis} />
              </Typography>
              <Explanation text={step.formula} />
            </Box>
          ))}
        </Box>
      ))}

      <Box sx={{ mt: 3 }}>
        <ExpertNormsTable clientId={clientId} load={getAdminClientExpertNorms} />
      </Box>
    </Box>
  );
};
